// frontend/src/pages/MatchScoring/PreInningsScreen.js
//
// Pre-innings opener picker.
// Calls POST /scorer/matches/:id/innings/start with chosen striker/non-striker/bowler.
// `inningsNumber` (1 or 2) decides which side bats — for now we read from props
// so the parent can hand us the right side based on toss decisions.

import React, { useMemo, useState } from 'react';
import { confirmCondition } from './conditions';
import api from '../../utils/api';

export default function PreInningsScreen({
  matchId,
  inningsNumber,
  battingSide,
  bowlingSide,
  maxOvers,
  targetRuns,        // null for innings 1; set automatically by parent for innings 2
  battingSquad,      // [{member_id, ...}]
  bowlingSquad,
  membersById,
  conditionsById,    // member_id -> { no_bowling, no_keeping } for this match
  onStarted,         // callback after innings/start succeeds
}) {
  const [strikerId, setStrikerId]   = useState('');
  const [nsId, setNsId]             = useState('');
  const [bowlerId, setBowlerId]     = useState('');
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState(null);

  // Sort by batting_order then name for the batting picker, by name for bowling.
  const battingOptions = useMemo(() => {
    return [...battingSquad].sort((a, b) => {
      const ao = a.batting_order ?? 99, bo = b.batting_order ?? 99;
      if (ao !== bo) return ao - bo;
      const an = membersById.get(a.member_id)?.display_name || '';
      const bn = membersById.get(b.member_id)?.display_name || '';
      return an.localeCompare(bn);
    });
  }, [battingSquad, membersById]);

  const bowlingOptions = useMemo(() => {
    return [...bowlingSquad].filter(s => s.can_bowl !== false).sort((a, b) => {
      const an = membersById.get(a.member_id)?.display_name || '';
      const bn = membersById.get(b.member_id)?.display_name || '';
      return an.localeCompare(bn);
    });
  }, [bowlingSquad, membersById]);

  function nameOf(id) { return membersById.get(id)?.display_name || id?.slice(0, 8); }

  const canStart = strikerId && nsId && bowlerId && strikerId !== nsId;

  async function start() {
    if (!canStart) return;
    if (!(await confirmCondition({ matchId, memberId: bowlerId, kind: 'bowling', name: nameOf(bowlerId), overNumber: 0, conditionsById }))) return;
    setBusy(true); setErr(null);
    try {
      const res = await api.post(`/scorer/matches/${matchId}/innings/start`, {
        innings_number: inningsNumber,
        batting_side:   battingSide,
        bowling_side:   bowlingSide,
        max_overs:      maxOvers,
        target_runs:    targetRuns ?? null,
        opening_striker_id:     strikerId,
        opening_non_striker_id: nsId,
        opening_bowler_id:      bowlerId,
      });
      if (res.data?.ok) onStarted?.();
      else setErr('Server returned a non-ok response.');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to start innings');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sc-stage">
      <h2>Innings {inningsNumber} — pick openers</h2>
      <div className="sc-sub">
        Side {battingSide} batting · {maxOvers} overs
        {targetRuns ? ` · target ${targetRuns}` : ''}
      </div>

      <div className="sc-pick-row">
        <label className="sc-pick-label">Striker</label>
        <select className="sc-pick-select" value={strikerId} onChange={e => setStrikerId(e.target.value)}>
          <option value="">— pick —</option>
          {battingOptions.map(s => (
            <option key={s.member_id} value={s.member_id}>{nameOf(s.member_id)}</option>
          ))}
        </select>
      </div>

      <div className="sc-pick-row">
        <label className="sc-pick-label">Non-striker</label>
        <select className="sc-pick-select" value={nsId} onChange={e => setNsId(e.target.value)}>
          <option value="">— pick —</option>
          {battingOptions.filter(s => s.member_id !== strikerId).map(s => (
            <option key={s.member_id} value={s.member_id}>{nameOf(s.member_id)}</option>
          ))}
        </select>
      </div>

      <div className="sc-pick-row">
        <label className="sc-pick-label">Opening bowler</label>
        <select className="sc-pick-select" value={bowlerId} onChange={e => setBowlerId(e.target.value)}>
          <option value="">— pick —</option>
          {bowlingOptions.map(s => (
            <option key={s.member_id} value={s.member_id}>{nameOf(s.member_id)}{conditionsById?.get(s.member_id)?.no_bowling ? ' ⚠ said No bowling' : ''}</option>
          ))}
        </select>
      </div>

      {err && <div className="sc-error-box">{err}</div>}

      <button className="sc-stage-cta" disabled={!canStart || busy} onClick={start}>
        {busy ? 'Starting…' : `▶ Start innings ${inningsNumber}`}
      </button>
    </div>
  );
}
